import React from 'react'
import routerConfigFunc from 'routerConfig'

import { lazy } from '@loadable/component'

import { getEnvParam } from 'env'
import { loadWithChunkRetry } from 'utils/chunkReload'
import { isDevelop } from 'vars'

interface LoadableConfig {
  fallback: React.ReactElement
}

interface Route {
  path: string
  Component: any
  exact?: boolean
}

interface RouterConfig {
  router: {
    default: (config: LoadableConfig) => Route[]
  }
}

interface RouteInfo {
  prev: Route[] | null
  map: Record<string, Route> | null
}

const config: LoadableConfig = {
  // fallback: <Spin style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}} />,
  fallback: <div />,
}

const AuthLogin = lazy(
  loadWithChunkRetry(() => import('pages/auth/login')),
  config,
)
const AuthCallback = lazy(
  loadWithChunkRetry(() => import('pages/auth/callback')),
  config,
)
const AuthRedirect = lazy(
  loadWithChunkRetry(() => import('pages/auth/redirect')),
  config,
)
const AuthLoginByKey = lazy(
  loadWithChunkRetry(() => import('pages/auth/loginByKey')),
  config,
)
const Auth404 = lazy(
  loadWithChunkRetry(() => import('pages/auth/404')),
  config,
)
const Auth500 = lazy(
  loadWithChunkRetry(() => import('pages/auth/500')),
  config,
)
const AuthAccessDenied = lazy(
  loadWithChunkRetry(() => import('pages/auth/accessDenied')),
  config,
)
const AuthRestartRequired = lazy(
  loadWithChunkRetry(() => import('pages/auth/restartRequired')),
  config,
)
const AuthEntry = lazy(
  loadWithChunkRetry(() => import('pages/auth/entry')),
  config,
)
const PlainThreadViewer = lazy(
  loadWithChunkRetry(() => import('pages/threadViewer')),
  config,
)
const TestPage = lazy(
  loadWithChunkRetry(() => import('pages/test')),
  config,
)
const TestIdentity = lazy(
  loadWithChunkRetry(() => import('pages/test/identity')),
  config,
)

// dev/local 전용 테스트 페이지 (로그인 없이 접근 가능). isDevelop 이 false 인
// 프로덕션·staging·qc 빌드에서는 삼항의 import() 가 dead-code 로 제거되어 청크가 생성되지 않는다.
const DevMailView = isDevelop
  ? lazy(
      loadWithChunkRetry(() => import('pages/dev/DevMailView')),
      config,
    )
  : null
const DevBizErrorModal = isDevelop
  ? lazy(
      loadWithChunkRetry(() => import('pages/dev/BizErrorModalTest')),
      config,
    )
  : null
// popup(native window) 편집기 테스트 페이지. default export 는 connect 로 dispatch/user 를
// 주입받는 컴포넌트라 런타임엔 props 가 없다.
const DevPopupEditor = isDevelop
  ? lazy(
      loadWithChunkRetry(() => import('pages/test')),
      config,
    )
  : null

export function getDefaultRoutes(): Route[] {
  let defaultRoutes: Route[] = [
    {
      path: '/auth/login',
      Component: AuthLogin,
      exact: true,
    },
    {
      path: '/auth/callback',
      Component: AuthCallback,
      exact: true,
    },
    {
      path: '/auth/redirect',
      Component: AuthRedirect,
      exact: true,
    },
    {
      path: '/auth/sso/:key',
      Component: AuthLoginByKey,
      exact: true,
    },
    // {
    //   path: '/auth/forgot-password',
    //   Component: lazy(() => import('pages/auth/forgot-password'), config),
    //   exact: true,
    // },
    // {
    //   path: '/auth/register',
    //   Component: lazy(() => import('pages/auth/register'), config),
    //   exact: true,
    // },
    {
      path: '/auth/404',
      Component: Auth404,
      exact: true,
    },
    {
      path: '/auth/500',
      Component: Auth500,
      exact: true,
    },
    {
      path: '/auth/access-denied',
      Component: AuthAccessDenied,
      exact: true,
    },
    {
      path: '/auth/restart-required',
      Component: AuthRestartRequired,
      exact: true,
    },
    {
      path: '/auth/entry',
      Component: AuthEntry,
      exact: true,
    },
  ]

  if (isDevelop) {
    // 로그인 없이 접근 가능한 dev 전용 테스트 페이지 (/dev/*).
    // Layout(needAuth) 및 router 초기화 가드에서 '/dev' prefix 예외 처리와 함께 동작한다.
    defaultRoutes = [
      ...defaultRoutes,
      {
        path: '/dev/mailview',
        Component: DevMailView,
      },
      {
        path: '/dev/biz-error-modal',
        Component: DevBizErrorModal,
      },
      {
        path: '/dev/popup-test',
        Component: DevPopupEditor,
      },
    ]
  }

  if (!__ENV_PRODUCTION__) {
    defaultRoutes = [
      ...defaultRoutes,
      {
        path: '/plain/threadViewer',
        Component: PlainThreadViewer,
      },
      {
        path: '/test',
        Component: TestPage,
      },
      {
        path: '/test/identity',
        Component: TestIdentity,
      },
    ]
  } else {
    const userEmail = getEnvParam('userEmail')
    if (
      userEmail != null &&
      (userEmail.endsWith('@jiran.com') ||
        userEmail.endsWith('@nextintelligence.ai') ||
        userEmail.endsWith('@officemail.app'))
    ) {
      defaultRoutes = [
        ...defaultRoutes,
        {
          path: '/test',
          Component: TestPage,
        },
      ]
    }
  }

  return defaultRoutes
}

const routeInfo: RouteInfo = {
  prev: null,
  map: null,
}

export function clearRoutes(): void {
  routeInfo.prev = null
  routeInfo.map = null
}

export default async function getRoutes(): Promise<Route[]> {
  const routerConfig: RouterConfig = await routerConfigFunc()
  const defaultRoutes = getDefaultRoutes()
  const myRoutes = [...defaultRoutes, ...routerConfig.router.default(config)]

  let isChanged = true
  if (routeInfo.prev != null) {
    isChanged = routeInfo.prev.length !== myRoutes.length
    if (isChanged === false) {
      if (routeInfo.map == null) {
        routeInfo.map = {}
        routeInfo.prev.forEach((item) => {
          routeInfo.map![item.path] = item
        })
      }
      isChanged = myRoutes.some((route) => {
        return routeInfo.map![route.path] == null
      })
    }
  }

  if (isChanged === true) {
    routeInfo.prev = myRoutes
    routeInfo.map = null
  } else {
    // routeInfo.prev.length = 0;
    // myRoutes.forEach((route) => {
    //   routeInfo.prev.push(route);
    // });
    return routeInfo.prev
  }

  return myRoutes
}
